import { ref, computed, reactive } from 'vue'
import { useStore } from 'vuex'
import { useApi } from './useApi'

const LOCALSTORAGE_KEY = 'current_estimate_draft'

export function useEstimates() {
  const store = useStore()
  const { get, post, patch, delete: del, loading, error } = useApi()
  // A restore has its own loading, so the list isn't covered: its row says "Restoring..." (T5)
  const { post: postRestore } = useApi()

  // Store getters
  const currentEstimate = computed(() => store.getters['estimates/currentEstimate'])
  const savedEstimates = computed(() => store.getters['estimates/savedEstimates'])
  const pagination = computed(() => store.getters['estimates/pagination'])
  const filters = computed(() => store.getters['estimates/filters'])
  const totalItems = computed(() => store.getters['estimates/totalItems'])
  const currentPage = computed(() => store.getters['estimates/currentPage'])
  const totalPages = computed(() => store.getters['estimates/totalPages'])
  const entriesPerPage = computed(() => store.getters['estimates/entriesPerPage'])
  const currentEstimateItemCount = computed(() => store.getters['estimates/currentEstimateItemCount'])
  const currentEstimateTotal = computed(() => store.getters['estimates/currentEstimateTotal'])
  const hasItemsInCart = computed(() => store.getters['estimates/hasItemsInCart'])

  /**
   * Calculate pricing tier for a given quantity
   */
  const calculateTierPrice = (quantity, pricingTiers) => {
    if (!pricingTiers || pricingTiers.length === 0) {
      return { unit_price: 0, line_total: 0, tier_min_qty: 0 }
    }

    // Sort tiers by minimum quantity (descending)
    const sortedTiers = [...pricingTiers].sort((a, b) => {
      const minQtyA = a.minimumQty || a.minimum_qty || 0
      const minQtyB = b.minimumQty || b.minimum_qty || 0
      return minQtyB - minQtyA
    })

    // Find the appropriate tier
    let selectedTier = null
    for (const tier of sortedTiers) {
      const minQty = tier.minimumQty || tier.minimum_qty || 0
      if (quantity >= minQty) {
        selectedTier = tier
        break
      }
    }

    // If no tier found, use the last tier (lowest minimum)
    if (!selectedTier) {
      selectedTier = sortedTiers[sortedTiers.length - 1]
    }

    const unitPrice = selectedTier.amount || 0
    const tierMinQty = selectedTier.minimumQty || selectedTier.minimum_qty || 0
    const lineTotal = quantity * unitPrice

    return {
      unit_price: parseFloat(unitPrice).toFixed(2),
      line_total: parseFloat(lineTotal).toFixed(2),
      tier_min_qty: tierMinQty
    }
  }

  /**
   * Fetch current draft estimate from API
   */
  const fetchCurrentEstimate = async () => {
    try {
      const response = await get('/estimates/current')

      if (response.success) {
        store.dispatch('estimates/setCurrentEstimate', response.data)
        // Sync to localStorage
        saveToLocalStorage(response.data)
      }

      return response
    } catch (err) {
      console.error('Error fetching current estimate:', err)
      // Try to load from localStorage
      loadFromLocalStorage()
      throw err
    }
  }

  /**
   * Add item to current estimate
   */
  const addItemToEstimate = async (itemData) => {
    try {
      const response = await post('/estimates/current/items', itemData)

      if (response.success) {
        store.dispatch('estimates/setCurrentEstimate', response.data.estimate)
        // Sync to localStorage
        saveToLocalStorage(response.data.estimate)
      }

      return response
    } catch (err) {
      console.error('Error adding item to estimate:', err)
      throw err
    }
  }

  /**
   * Add many items in one call. All-or-nothing on the server: if any row
   * fails, nothing is added and the message names the row.
   */
  const addItemsToEstimate = async (items) => {
    try {
      const response = await post('/estimates/current/items/bulk', { items })

      if (response.success) {
        store.dispatch('estimates/setCurrentEstimate', response.data.estimate)
        saveToLocalStorage(response.data.estimate)
      }

      return response
    } catch (err) {
      console.error('Error adding items to estimate:', err)
      throw err
    }
  }

  /**
   * Remove item from current estimate
   */
  const removeItemFromEstimate = async (itemId) => {
    try {
      const response = await del(`/estimates/current/items/${itemId}`)

      if (response.success) {
        store.dispatch('estimates/setCurrentEstimate', response.data.estimate)
        saveToLocalStorage(response.data.estimate)
      }

      return response
    } catch (err) {
      console.error('Error removing item from estimate:', err)
      throw err
    }
  }

  /**
   * Update item quantity in current estimate
   */
  const updateEstimateItemQuantity = async (itemId, quantity) => {
    try {
      const response = await patch(`/estimates/current/items/${itemId}`, { quantity })

      if (response.success) {
        // Update the item in the store
        store.dispatch('estimates/updateEstimateItem', {
          item: response.data.item,
          estimate: response.data.estimate
        })
        // Sync to localStorage
        const updatedEstimate = currentEstimate.value
        saveToLocalStorage(updatedEstimate)
      }

      return response
    } catch (err) {
      console.error('Error updating item quantity:', err)
      throw err
    }
  }

  /**
   * Save estimate (lock with ID and timestamp)
   */
  const saveEstimate = async () => {
    try {
      const response = await post('/estimates/current/save')

      if (response.success) {
        // Clear current estimate from store and localStorage
        store.dispatch('estimates/clearCurrentEstimate')
        clearLocalStorage()
        // Fetch new draft estimate
        await fetchCurrentEstimate()
      }

      return response
    } catch (err) {
      console.error('Error saving estimate:', err)
      throw err
    }
  }

  /**
   * Fetch all saved estimates for the user. With filterOptions.deleted, the "Show deleted" list
   * (T5): 'mine' = the user's own, 'everyone' = an admin's list of everyone's.
   */
  const fetchSavedEstimates = async (filterOptions = {}) => {
    try {
      const params = new URLSearchParams()

      if (filterOptions.search) params.append('search', filterOptions.search)
      if (filterOptions.per_page) params.append('per_page', filterOptions.per_page)
      if (filterOptions.page) params.append('page', filterOptions.page)

      if (filterOptions.deleted === 'everyone') {
        return await fetchEveryonesDeleted(params)
      }

      if (filterOptions.deleted) params.append('deleted', '1')

      const response = await get(`/estimates?${params.toString()}`)

      if (response.success) {
        store.dispatch('estimates/setSavedEstimates', {
          estimates: response.data.estimates,
          pagination: response.data.pagination
        })
      }

      return response
    } catch (err) {
      console.error('Error fetching saved estimates:', err)
      throw err
    }
  }

  /**
   * Everyone's deleted estimates, for admins (T5). It's a new endpoint ({ data, meta }), so its
   * paging is turned into the list's own shape.
   */
  const fetchEveryonesDeleted = async (params) => {
    const response = await get(`/deleted-estimates?${params.toString()}`)
    const meta = response.meta

    store.dispatch('estimates/setSavedEstimates', {
      estimates: response.data,
      pagination: {
        current_page: meta.current_page,
        per_page: meta.per_page,
        total: meta.total,
        last_page: meta.last_page,
        from: meta.from ?? 0,
        to: meta.to ?? 0,
        has_more_pages: meta.current_page < meta.last_page
      }
    })

    return response
  }

  /**
   * Bring back a deleted estimate (T5): your own, or for an admin, anyone's. Then the list
   * reloads. A failed reload never makes the restore look failed.
   */
  const restoreEstimate = async (id, everyone = false) => {
    let response
    try {
      response = await postRestore(everyone ? `/deleted-estimates/${id}/restore` : `/estimates/${id}/restore`)
    } catch (err) {
      console.error('Error restoring estimate:', err)
      throw err
    }

    try {
      await fetchSavedEstimates(filters.value)
    } catch (err) {
      console.error('Error refreshing estimates:', err)
    }

    return response
  }

  /**
   * "Show deleted" starts unticked each time the Estimates page opens (T5)
   */
  const resetDeleted = () => {
    store.dispatch('estimates/setFilters', { deleted: false })
  }

  /**
   * Get a specific estimate by ID
   */
  const getEstimate = async (id) => {
    try {
      const response = await get(`/estimates/${id}`)
      return response
    } catch (err) {
      console.error('Error fetching estimate:', err)
      throw err
    }
  }

  /**
   * Delete a saved estimate softly, with the optional reason the rep typed (T3)
   */
  const deleteEstimate = async (id, reason = '') => {
    try {
      const response = await del(`/estimates/${id}`, { data: { reason } })

      if (response.success) {
        // Refresh saved estimates list
        await fetchSavedEstimates({
          search: filters.value.search,
          per_page: filters.value.per_page,
          page: filters.value.page
        })
      }

      return response
    } catch (err) {
      console.error('Error deleting estimate:', err)
      throw err
    }
  }

  /**
   * Sync a saved estimate to HubSpot
   */
  const syncEstimateToHubSpot = async (id) => {
    try {
      const response = await post(`/estimates/${id}/sync-hubspot`)

      if (response.success) {
        // Refresh saved estimates list to get updated sync status
        await fetchSavedEstimates({
          search: filters.value.search,
          per_page: filters.value.per_page,
          page: filters.value.page
        })
      }

      return response
    } catch (err) {
      console.error('Error syncing estimate to HubSpot:', err)
      throw err
    }
  }

  /**
   * Set filters and fetch saved estimates
   */
  const setFilters = (newFilters) => {
    store.dispatch('estimates/setFilters', newFilters)
    return fetchSavedEstimates({
      ...filters.value,
      ...newFilters,
      page: 1 // Reset to first page when filters change
    })
  }

  /**
   * Change page
   */
  const setPage = (page) => {
    store.dispatch('estimates/setFilters', { page })
    return fetchSavedEstimates({
      ...filters.value,
      page
    })
  }

  /**
   * Set entries per page
   */
  const setPerPage = (perPage) => {
    store.dispatch('estimates/setFilters', { per_page: perPage, page: 1 })
    return fetchSavedEstimates({
      ...filters.value,
      per_page: perPage,
      page: 1
    })
  }

  /**
   * LocalStorage management
   */
  const saveToLocalStorage = (estimate) => {
    try {
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(estimate))
    } catch (err) {
      console.error('Error saving to localStorage:', err)
    }
  }

  const loadFromLocalStorage = () => {
    try {
      const data = localStorage.getItem(LOCALSTORAGE_KEY)
      if (data) {
        const estimate = JSON.parse(data)
        store.dispatch('estimates/setCurrentEstimate', estimate)
        return estimate
      }
    } catch (err) {
      console.error('Error loading from localStorage:', err)
    }
    return null
  }

  const clearLocalStorage = () => {
    try {
      localStorage.removeItem(LOCALSTORAGE_KEY)
    } catch (err) {
      console.error('Error clearing localStorage:', err)
    }
  }

  /**
   * Sync localStorage with backend on app init
   */
  const syncWithBackend = async () => {
    try {
      await fetchCurrentEstimate()
    } catch (err) {
      // If backend fails, try localStorage
      loadFromLocalStorage()
    }
  }

  return {
    // State
    currentEstimate,
    savedEstimates,
    pagination,
    filters,
    loading,
    error,

    // Computed
    totalItems,
    currentPage,
    totalPages,
    entriesPerPage,
    currentEstimateItemCount,
    currentEstimateTotal,
    hasItemsInCart,

    // Methods
    calculateTierPrice,
    fetchCurrentEstimate,
    addItemToEstimate,
    addItemsToEstimate,
    removeItemFromEstimate,
    updateEstimateItemQuantity,
    saveEstimate,
    fetchSavedEstimates,
    getEstimate,
    deleteEstimate,
    restoreEstimate,
    resetDeleted,
    syncEstimateToHubSpot,
    setFilters,
    setPage,
    setPerPage,
    syncWithBackend,
    saveToLocalStorage,
    loadFromLocalStorage,
    clearLocalStorage
  }
}
